/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry as _withAntiBotRetry } from '@shared/utils/with-antibot-retry';
import {
  FF_APP_CHAVES_NA_MAO, FF_APP_FACEBOOK, FF_APP_INSTAGRAM_NATIVE, FF_APP_OLX, FF_APP_PORTAL_FEEDS, FF_APP_PORTALS_INTEGRATION,
} from '@/shared/utils/feature-flag';
import { API_BASE } from './constants';
import { buildPublishPayload, withSpendConsent } from './publish-payload';
import { setMetaMinDailyBudgetCents } from '@/shared/utils/ad-dialog-helpers';
import {
  isScheduledAtInFuture,
  findLeadAdCreated,
  findLeadAdFailure,
  scheduleErrorMessage,
} from './publish-helpers';
import type { Results } from './publish-helpers';
import { portalsAvailableToPublish } from '@/shared/utils/portal-publish';
import type { AdErrorInfo, AdSpendItem, AdSpendSummary } from '../ad-dialogs/ad-dialog-types';
import { adReadinessFrom, NOTHING_CONFIGURED, type AdReadiness } from '@/shared/utils/ad-readiness';

const PORTAL_LABELS: Record<string, string> = {
  olx: 'OLX',
  zap: 'ZAP Imoveis',
  vivareal: 'Viva Real',
  chavesnamao: 'Chaves na Mao',
  facebook: 'Facebook',
  instagram: 'Instagram',
};

const ACTIVE_STATUSES = new Set(['published', 'queued', 'scheduled']);
const INTEGRATION_PORTAL_IDS = new Set(['olx', 'zap', 'vivareal', 'chavesnamao']);
const FACEBOOK_ALIASES = new Set(['facebook_feed', 'facebook_marketplace']);

type Translate = (key: string, opts?: Record<string, unknown>) => string;
type PortalResult = { status: string; reason?: string };

/**
 * Classifies per-portal results into successes and failures.
 * "queued" is treated as success (included in feed / scheduled).
 */
export function classifyPublishResults(results: Results): {
  successes: string[];
  failures: Array<{ portal: string; reason?: string }>;
} {
  const successes: string[] = [];
  const failures: Array<{ portal: string; reason?: string }> = [];
  for (const [portal, r] of Object.entries(results)) {
    if (ACTIVE_STATUSES.has(r.status)) successes.push(portal);
    else failures.push({ portal, reason: r.reason });
  }
  return { successes, failures };
}

/**
 * Formats a single failure entry as "Label" or "Label (reason)".
 */
function formatFailureLabel(
  entry: { portal: string; reason?: string },
  t: Translate,
): string {
  const label = PORTAL_LABELS[entry.portal] ?? entry.portal;
  const reasonKey = entry.reason ? `publish.reason.${entry.reason}` : null;
  const reasonLabel = reasonKey ? t(reasonKey) : null;
  const hasReason = reasonLabel && reasonLabel !== reasonKey;
  return hasReason ? `${label} (${reasonLabel})` : label;
}

/**
 * Joins failure entries into a comma-separated human-readable list.
 */
function joinFailures(failures: Array<{ portal: string; reason?: string }>, t: Translate): string {
  return failures.map((entry) => formatFailureLabel(entry, t)).join(', ');
}

/**
 * Builds the appropriate toast based on per-portal results and triggers it.
 * Returns true if at least one portal succeeded (caller should proceed with onPublished).
 */
export function showPublishResultToast(results: Results, t: Translate): boolean {
  const { successes, failures } = classifyPublishResults(results);

  if (successes.length === 0) {
    toast.error(t('publish.allFailedToast', { portals: joinFailures(failures, t) }));
    return false;
  }

  if (failures.length > 0) {
    const successLabels = successes.map((p) => PORTAL_LABELS[p] ?? p).join(', ');
    toast.warning(
      t('publish.partialSuccessToast', { successes: successLabels, failures: joinFailures(failures, t) }),
    );
    return true;
  }

  toast.success(t('publish.successToast'));
  return true;
}

/**
 * Builds the request headers for publish calls (requires X-Anti-Bot).
 * Uses a publish-specific action so the SDK does not reuse a spent token.
 */
function buildHeaders(opts?: { reload?: boolean }): Promise<Record<string, string>> {
  return buildPortalIntegrationHeaders('portal_publish', opts);
}

/**
 * Wraps a request with anti-bot retry handling.
 */
function withAntiBotRetry<T>(fn: (headers: Record<string, string>) => Promise<T>): Promise<T> {
  return _withAntiBotRetry(fn, buildHeaders);
}

/**
 * Integration portal ids enabled by the account, when that feature is on.
 *
 * @param enabledPortals - The portals selected in the dialog.
 * @returns The integration ids to publish to, empty when the feature is off.
 */
function integrationPortalIds(enabledPortals: string[]): string[] {
  if (!FF_APP_PORTALS_INTEGRATION) return [];
  return enabledPortals.filter((p) => INTEGRATION_PORTAL_IDS.has(p) && integrationPortalOn(p));
}

/**
 * Whether the flag of an integration portal is on (Chaves na Mão has its own).
 *
 * @param portal - The integration portal id.
 * @returns True when the portal may be published to.
 */
function integrationPortalOn(portal: string): boolean {
  if (portal === 'olx') return FF_APP_OLX;
  if (portal === 'chavesnamao') return FF_APP_CHAVES_NA_MAO || FF_APP_PORTAL_FEEDS;
  return FF_APP_PORTAL_FEEDS;
}

/**
 * The Facebook feed portal id, when Facebook publishing is enabled and selected.
 *
 * @param enabledPortals - The portals selected in the dialog.
 * @returns The feed id, or null when Facebook is off or unselected.
 */
function facebookFeedId(enabledPortals: string[]): string | null {
  return FF_APP_FACEBOOK && enabledPortals.includes('facebook') ? 'facebook_feed' : null;
}

/**
 * Whether Instagram publishing is available for this account.
 *
 * Requires `enabledPortals.includes('instagram')` -- the same condition
 * portal-publish.ts's shouldShowInstagramPortal() uses to decide whether the
 * dialog even shows an Instagram checkbox -- in addition to a linked account
 * id. Checking igAccountId alone let a leftover/stale id (e.g. from a
 * Facebook Page that happens to have some Instagram Business account linked
 * on Meta's side, independent of whether the agency ever enabled Instagram
 * publishing in Housien) silently add 'instagram' to the selection and the
 * publish payload even when its checkbox was never rendered: reproduced live
 * as "Publish on 2 portals" with only a single, checked Facebook row visible.
 */
function instagramAvailable(enabledPortals: string[], igAccountId: string | null): boolean {
  return (FF_APP_FACEBOOK || FF_APP_INSTAGRAM_NATIVE)
    && enabledPortals.includes('instagram')
    && Boolean(igAccountId);
}

/**
 * Lists the portal ids that could be selected given feature flags and account state.
 *
 * @param enabledPortals - The agency's enabled integration portal ids.
 * @param igAccountId - The connected Instagram account id, if any.
 * @returns The portal ids available for selection.
 */
export function availablePortalIds(enabledPortals: string[], igAccountId: string | null): string[] {
  const ids = [...integrationPortalIds(enabledPortals)];
  const facebookId = facebookFeedId(enabledPortals);
  if (facebookId) ids.push(facebookId);
  if (instagramAvailable(enabledPortals, igAccountId)) ids.push('instagram');
  return ids;
}

/**
 * Computes the initially selected portals.
 */
export function computeInitialSelected(
  normalizedStatus: Record<string, string>,
  enabledPortals: string[],
  igAccountId: string | null,
): string[] {
  return availablePortalIds(enabledPortals, igAccountId).filter((id) => {
    const backendKey = FACEBOOK_ALIASES.has(id) ? 'facebook' : id;
    const status = normalizedStatus[backendKey] ?? normalizedStatus[id];
    return !ACTIVE_STATUSES.has(status ?? '');
  });
}

/**
 * Normalizes the raw publish-status payload into a portal → status map.
 */
function normalizeStatus(portals: Record<string, unknown>): Record<string, string> {
  const normalized: Record<string, string> = {};
  for (const [k, v] of Object.entries(portals)) {
    normalized[k] = typeof v === 'string' ? v : (v as { status?: string })?.status ?? 'published';
  }
  return normalized;
}

/**
 * Builds auth-only headers for safe GET portal calls (backend skips anti-bot).
 */
export async function authOnlyHeaders(): Promise<Record<string, string>> {
  const { getValidAuthToken } = await import('@services/auth/auth');
  const token = await getValidAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetches publish-status and agency settings for the property.
 * GET endpoints skip anti-bot on the backend — auth-only headers avoid minting
 * a reCAPTCHA token before the user clicks Publish.
 */
async function fetchPublishState(agencyId: string, propertyId: string) {
  const headers = await authOnlyHeaders();
  return Promise.all([
    axios
      .get(
        `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publish-status`,
        { headers },
      )
      .catch(() => null),
    axios
      .get(`${API_BASE}/portal-integration/agencies/${agencyId}/settings`, { headers })
      .catch(() => null),
  ]);
}

/**
 * Merges publish results into the saved status map (successes only).
 */
function mergeStatusFromResults(saved: Record<string, string>, publishResults: Results): Record<string, string> {
  const next = { ...saved };
  for (const [portal, result] of Object.entries(publishResults)) {
    if (ACTIVE_STATUSES.has(result.status)) next[portal] = result.status;
  }
  return next;
}

interface UsePublishDialogParams {
  propertyId: string;
  agencyId: string;
  onPublished?: () => void;
  onClose: () => void;
  t: Translate;
  fbCreateLeadAd: boolean;
  igCreateLeadAd?: boolean;
  hasImages?: boolean;
  confirmSpend: (summary: AdSpendSummary) => Promise<boolean>;
  showAdError: (error: AdErrorInfo) => void;
  /** The phone the OLX ad shows (digits), picked in the dialog; null when OLX is not selected or none is valid. */
  olxPhone?: string | null;
  /** The main deal of the OLX ad (`sale` / `rental`), picked in the dialog for a property offered for both; null when there was nothing to choose. */
  olxTransaction?: string | null;
}

interface PublishState {
  savedStatus: Record<string, string>;
  setSavedStatus: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  setEnabledPortals: React.Dispatch<React.SetStateAction<string[]>>;
  setFbHasPaymentMethod: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIgAccountId: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setFbLeadAdBudget: React.Dispatch<React.SetStateAction<number>>;
  setIgLeadAdBudget: React.Dispatch<React.SetStateAction<number>>;
  /** How many days a Facebook / Instagram Lead Ad runs, from the agency's saved defaults. */
  setLeadAdDurations?: React.Dispatch<React.SetStateAction<LeadAdDurations>>;
  /** What the agency must still fill in before it can create a Lead Ad, per platform. */
  setAdReadiness?: React.Dispatch<React.SetStateAction<AdReadiness>>;
  /** Why OLX cannot publish now (no plan, unconfirmed account, reconnect), or null. */
  setOlxBlockReason?: React.Dispatch<React.SetStateAction<string | null>>;
  /** When the OLX plan's ads come back, for the "limit reached" reason. */
  setOlxRenewsAt?: React.Dispatch<React.SetStateAction<string | null>>;
}

interface LeadAdDurations {
  fb: number;
  ig: number;
}

type PublishSettingsResponse = {
  data?: {
    enabled_portals?: unknown;
    connected_portals?: unknown;
    ig_account_id?: string | null;
    ig_native_connected?: unknown;
    ig_native_account_id?: string | null;
    fb_has_payment_method?: unknown;
    fb_connected?: unknown;
    fb_lead_ad_budget?: unknown;
    ig_lead_ad_budget?: unknown;
    fb_lead_ad_duration?: unknown;
    ig_lead_ad_duration?: unknown;
    ad_min_daily_budget_cents?: unknown;
    olx_block_reason?: unknown;
    olx_plan_renews_at?: unknown;
  };
} | null;

/** Converts a settings budget field (cents) to whole reais, or null when absent/invalid. */
function budgetReaisFromCents(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value / 100)
    : null;
}

/** Converts a settings duration field to whole days (1-90), or null when absent/invalid. */
function durationDaysFrom(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 90 ? value : null;
}

/**
 * Normalizes the raw settings response into the fields `applyLoadedPublishState` needs.
 */
function normalizeSettingsResponse(settingsRes: PublishSettingsResponse) {
  const enabled = settingsRes?.data?.enabled_portals;
  const connected = settingsRes?.data?.connected_portals;
  return {
    enabledList: Array.isArray(enabled) ? (enabled as string[]) : [],
    connectedList: Array.isArray(connected) ? (connected as string[]) : null,
    fbConnected: settingsRes?.data?.fb_connected === true,
    igNativeConnected: settingsRes?.data?.ig_native_connected === true,
    igId: settingsRes?.data?.ig_account_id ?? settingsRes?.data?.ig_native_account_id ?? null,
    fbHasPaymentMethod: settingsRes?.data?.fb_has_payment_method,
    fbLeadAdBudget: budgetReaisFromCents(settingsRes?.data?.fb_lead_ad_budget),
    igLeadAdBudget: budgetReaisFromCents(settingsRes?.data?.ig_lead_ad_budget),
    fbLeadAdDuration: durationDaysFrom(settingsRes?.data?.fb_lead_ad_duration),
    igLeadAdDuration: durationDaysFrom(settingsRes?.data?.ig_lead_ad_duration),
  };
}

/**
 * Applies the normalized Lead Ad budget / duration fields to the hook setters.
 */
function applyLeadAdSettings(
  state: PublishState,
  values: {
    fbLeadAdBudget: number | null;
    igLeadAdBudget: number | null;
    fbLeadAdDuration: number | null;
    igLeadAdDuration: number | null;
  },
): void {
  if (values.fbLeadAdBudget !== null) state.setFbLeadAdBudget(values.fbLeadAdBudget);
  if (values.igLeadAdBudget !== null) state.setIgLeadAdBudget(values.igLeadAdBudget);
  state.setLeadAdDurations?.((previous) => ({
    fb: values.fbLeadAdDuration ?? previous.fb,
    ig: values.igLeadAdDuration ?? previous.ig,
  }));
}

/**
 * Applies the fetched status/settings responses to the hook's state setters.
 *
 * @param statusRes - The saved-publish-status response, if loaded.
 * @param settingsRes - The portal settings response, if loaded.
 * @param state - The hook's state setters to apply the loaded data to.
 */
function applyLoadedPublishState(
  statusRes: { data?: { portals?: Record<string, unknown> } } | null,
  settingsRes: PublishSettingsResponse,
  state: PublishState,
): void {
  const normalizedStatus = normalizeStatus(statusRes?.data?.portals || {});
  state.setSavedStatus(normalizedStatus);
  setMetaMinDailyBudgetCents(settingsRes?.data?.ad_min_daily_budget_cents);
  const {
    enabledList, connectedList, fbConnected, igNativeConnected, igId, fbHasPaymentMethod,
    fbLeadAdBudget, igLeadAdBudget, fbLeadAdDuration, igLeadAdDuration,
  } = normalizeSettingsResponse(settingsRes);
  const effective = portalsAvailableToPublish(enabledList, connectedList, fbConnected, igNativeConnected);
  state.setEnabledPortals(effective);
  if (fbHasPaymentMethod !== undefined) state.setFbHasPaymentMethod(Boolean(fbHasPaymentMethod));
  if (igId) state.setIgAccountId(igId);
  applyLeadAdSettings(state, { fbLeadAdBudget, igLeadAdBudget, fbLeadAdDuration, igLeadAdDuration });
  state.setAdReadiness?.(adReadinessFrom(settingsRes?.data as Record<string, unknown> | undefined));
  const olxBlock = typeof settingsRes?.data?.olx_block_reason === 'string' ? settingsRes.data.olx_block_reason : null;
  state.setOlxBlockReason?.(olxBlock);
  const renews = settingsRes?.data?.olx_plan_renews_at;
  state.setOlxRenewsAt?.(typeof renews === 'string' ? renews : null);
  const initial = computeInitialSelected(normalizedStatus, effective, igId);
  state.setSelected(olxBlock ? initial.filter((id) => id !== 'olx') : initial);
}

/**
 * Loads publish state into the hook's setters via an effect.
 */
function usePublishStateLoader(propertyId: string, agencyId: string, state: PublishState): void {
  const {
    setEnabledPortals, setFbHasPaymentMethod, setIgAccountId, setLoading, setSavedStatus, setSelected,
    setFbLeadAdBudget, setIgLeadAdBudget, setLeadAdDurations, setAdReadiness, setOlxBlockReason, setOlxRenewsAt,
  } = state;
  useEffect(() => {
    let cancelled = false;
    const setters: PublishState = {
      savedStatus: {},
      setEnabledPortals,
      setFbHasPaymentMethod,
      setIgAccountId,
      setLoading,
      setSavedStatus,
      setSelected,
      setFbLeadAdBudget,
      setIgLeadAdBudget,
      setLeadAdDurations,
      setAdReadiness,
      setOlxBlockReason,
      setOlxRenewsAt,
    };
    (async () => {
      try {
        const [statusRes, settingsRes] = await fetchPublishState(agencyId, propertyId);
        if (!cancelled) applyLoadedPublishState(statusRes, settingsRes, setters);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    propertyId, agencyId, setEnabledPortals, setFbHasPaymentMethod, setIgAccountId, setLoading, setSavedStatus,
    setSelected, setFbLeadAdBudget, setIgLeadAdBudget, setLeadAdDurations,
  ]);
}

/**
 * Handles Lead Ad outcomes: success toast and/or error dialog.
 * Returns true when the error dialog was shown (callers should skip further close logic).
 */
function handleLeadAdOutcomes(
  publishResults: Results,
  published: boolean,
  t: Translate,
  showAdError: (error: AdErrorInfo) => void,
  onClose: () => void,
): boolean {
  if (findLeadAdCreated(publishResults)) toast.success(t('adDialogs.leadAdCreated'));
  const leadAdFailure = findLeadAdFailure(publishResults);
  if (!leadAdFailure) return false;
  showAdError({
    kind: 'leadAd', detail: leadAdFailure.detail, code: leadAdFailure.code,
    onClose: published ? onClose : undefined,
  });
  return true;
}

interface PublishRequestArgs {
  agencyId: string;
  propertyId: string;
  selected: string[];
  hasImages: boolean;
  fbCreateLeadAd: boolean;
  fbLeadAdBudget: number;
  igCreateLeadAd: boolean;
  igLeadAdBudget: number;
  fbLeadAdDuration: number;
  igLeadAdDuration: number;
  scheduleEnabled: boolean;
  scheduledAt: string;
  olxPhone?: string | null;
  olxTransaction?: string | null;
  setResults: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
  setSavedStatus: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  t: Translate;
  onPublished?: () => void;
  onClose: () => void;
  confirmSpend: (summary: AdSpendSummary) => Promise<boolean>;
  showAdError: (error: AdErrorInfo) => void;
}

/**
 * Builds the publish payload and runs the spend-confirmation gate. Returns the
 * payload to post, or `null` when there is nothing to publish or the user
 * declined the Lead Ad spend.
 */
async function preparePublishPayload(
  args: PublishRequestArgs,
): Promise<Record<string, unknown> | null> {
  const payload = buildPublishPayload({
    selected: args.selected,
    propertyId: args.propertyId,
    hasImages: args.hasImages,
    fbCreateLeadAd: args.fbCreateLeadAd,
    fbLeadAdBudget: args.fbLeadAdBudget,
    igCreateLeadAd: args.igCreateLeadAd,
    igLeadAdBudget: args.igLeadAdBudget,
    fbLeadAdDuration: args.fbLeadAdDuration,
    igLeadAdDuration: args.igLeadAdDuration,
    scheduleEnabled: args.scheduleEnabled,
    scheduledAt: args.scheduledAt,
    olxPhone: args.olxPhone,
    olxTransaction: args.olxTransaction,
  });
  if (!payload) return null;
  const spend = leadAdSpendSummary(payload);
  if (spend && !(await args.confirmSpend(spend))) return null;
  return payload;
}

/**
 * Applies publish results to local state, toasts the outcome, and runs the
 * Lead Ad outcome + close logic.
 */
function finalizePublishResults(publishResults: Results, args: PublishRequestArgs): void {
  args.setResults(publishResults);
  args.setSavedStatus((prev) => mergeStatusFromResults(prev, publishResults));
  const published = showPublishResultToast(publishResults, args.t);
  if (published) args.onPublished?.();
  const errorShown = handleLeadAdOutcomes(publishResults, published, args.t, args.showAdError, args.onClose);
  if (!errorShown && published) args.onClose();
}

/**
 * Posts the publish payload and updates local result/status state.
 */
async function executePublishRequest(args: PublishRequestArgs): Promise<void> {
  const payload = await preparePublishPayload(args);
  if (!payload) return;
  const res = await withAntiBotRetry((headers) =>
    axios.post(`${API_BASE}/portal-integration/agencies/${args.agencyId}/publish`, withSpendConsent(payload), { headers }),
  );
  const publishResults: Results = res.data.results || {};
  finalizePublishResults(publishResults, args);
}

/**
 * When a scheduled publish starts, or undefined for a publish that starts now
 * (or whose schedule is not a valid date).
 */
function scheduledStart(payload: Record<string, unknown>): Date | undefined {
  if (typeof payload.scheduled_at !== 'string') return undefined;
  const start = new Date(payload.scheduled_at);
  return Number.isNaN(start.getTime()) ? undefined : start;
}

/** Builds a single Lead Ad spend item from its budget/duration payload fields. */
function leadAdItem(label: AdSpendItem['label'], budget: unknown, duration: unknown): AdSpendItem {
  return {
    label,
    dailyBudgetCents: Number(budget) || 0,
    durationDays: Number(duration) || 0,
  };
}

/**
 * The spend the user is about to commit to, one item per requested Lead Ad
 * (Facebook and Instagram each have their own budget and duration); null when
 * none is requested. A scheduled publish carries its start, because that is
 * when the Lead Ad is created and so when its days start counting.
 */
export function leadAdSpendSummary(payload: Record<string, unknown>): AdSpendSummary | null {
  const items: AdSpendItem[] = [];
  if (payload.fb_create_lead_ad) {
    items.push(leadAdItem('facebookLeadAd', payload.fb_lead_ad_budget, payload.fb_lead_ad_duration));
  }
  if (payload.ig_create_lead_ad) {
    items.push(leadAdItem('instagramLeadAd', payload.ig_lead_ad_budget, payload.ig_lead_ad_duration));
  }
  if (items.length === 0) return null;
  const startsAt = scheduledStart(payload);
  return { kind: 'leadAd', items, ...(startsAt ? { startsAt } : {}) };
}

/**
 * Manages the publish dialog state: loads portal availability/status, tracks
 * the user's selection, and performs the publish request.
 */
/**
 * Whether a schedule request is enabled, set, and already in the past.
 */
function isSchedulingInThePast(scheduleEnabled: boolean, scheduledAt: string): boolean {
  return scheduleEnabled && Boolean(scheduledAt) && !isScheduledAtInFuture(scheduledAt);
}

/**
 * Controller hook for the publish dialog: portal status/settings loading,
 * selection state, and the publish action (with optional scheduling).
 *
 * @param params - The property/agency context, translators, and Facebook lead-ad settings.
 * @returns Selection state, publish status/results, and the publish handler.
 */
export function usePublishDialog(params: UsePublishDialogParams) {
  const {
    propertyId, agencyId, onPublished, onClose, t, fbCreateLeadAd,
    igCreateLeadAd = false, hasImages = true, confirmSpend, showAdError, olxPhone = null, olxTransaction = null,
  } = params;
  const [selected, setSelected] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState<Record<string, unknown> | null>(null);
  const [savedStatus, setSavedStatus] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [enabledPortals, setEnabledPortals] = useState<string[]>([]);
  const [fbHasPaymentMethod, setFbHasPaymentMethod] = useState<boolean | null>(null);
  const [igAccountId, setIgAccountId] = useState<string | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  // Seeded from the agency's own fb_lead_ad_budget/ig_lead_ad_budget settings once
  // loaded (usePublishStateLoader below). There is no built-in amount: 0 until
  // loaded or when the agency has saved none, and then the Lead Ad panel asks it to
  // fill the settings in first (see `adReadiness`).
  const [fbLeadAdBudget, setFbLeadAdBudget] = useState(0);
  const [igLeadAdBudget, setIgLeadAdBudget] = useState(0);
  // How many days a Lead Ad runs, from the agency's saved settings (0 until loaded or when unsaved).
  const [leadAdDurations, setLeadAdDurations] = useState<LeadAdDurations>({ fb: 0, ig: 0 });
  const [adReadiness, setAdReadiness] = useState<AdReadiness>(NOTHING_CONFIGURED);
  const [olxBlockReason, setOlxBlockReason] = useState<string | null>(null);
  const [olxRenewsAt, setOlxRenewsAt] = useState<string | null>(null);

  usePublishStateLoader(propertyId, agencyId, {
    savedStatus, setSavedStatus, setSelected, setEnabledPortals, setFbHasPaymentMethod, setIgAccountId, setLoading,
    setFbLeadAdBudget, setIgLeadAdBudget, setLeadAdDurations, setAdReadiness, setOlxBlockReason, setOlxRenewsAt,
  });

  const handlePublish = useCallback(async () => {
    if (!selected.length) return;
    if (isSchedulingInThePast(scheduleEnabled, scheduledAt)) {
      reportError(new Error('schedule_past'), { message: t('publish.schedulePastError') });
      return;
    }
    setResults(null);
    setPublishing(true);
    try {
      await executePublishRequest({
        agencyId, propertyId, selected, hasImages, fbCreateLeadAd, fbLeadAdBudget,
        igCreateLeadAd, igLeadAdBudget,
        fbLeadAdDuration: leadAdDurations.fb, igLeadAdDuration: leadAdDurations.ig,
        scheduleEnabled, scheduledAt, olxPhone, olxTransaction, setResults, setSavedStatus, t, onPublished, onClose,
        confirmSpend, showAdError,
      });
    } catch (error) {
      reportError(error, { message: scheduleErrorMessage(error, t) });
    } finally {
      setPublishing(false);
    }
  }, [
    selected, propertyId, agencyId, t, fbCreateLeadAd, fbLeadAdBudget, igCreateLeadAd, igLeadAdBudget,
    leadAdDurations, onPublished, onClose, scheduleEnabled, scheduledAt, hasImages, setSavedStatus, confirmSpend, showAdError,
    olxPhone, olxTransaction,
  ]);

  return {
    selected, setSelected, publishing, results, savedStatus, loading,
    enabledPortals, fbHasPaymentMethod, igAccountId, handlePublish,
    scheduleEnabled, setScheduleEnabled, scheduledAt, setScheduledAt,
    fbLeadAdBudget, setFbLeadAdBudget, igLeadAdBudget, setIgLeadAdBudget, adReadiness, olxBlockReason, olxRenewsAt,
  };
}
